import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';

export type BannerType = 'info' | 'warn' | 'success' | 'error';

@Component({
  selector: 'app-inline-banner',
  template: `
    <output
      class="inline-banner"
      [ngClass]="'inline-banner--' + type"
      [attr.role]="type === 'error' ? 'alert' : null"
      [attr.aria-live]="type === 'error' ? 'assertive' : 'polite'"
    >
      <lucide-angular
        [name]="resolvedIcon"
        [size]="16"
        aria-hidden="true"
        class="inline-banner__icon"
      ></lucide-angular>
      <span class="inline-banner__content"><ng-content></ng-content></span>
      <button
        *ngIf="dismissable"
        type="button"
        class="inline-banner__close"
        aria-label="Dismiss"
        (click)="dismissed.emit()"
      >
        <lucide-angular name="x" [size]="14" aria-hidden="true"></lucide-angular>
      </button>
    </output>
  `,
  styles: [
    `
      .inline-banner {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 6px;
        font-size: 13px;
        line-height: 1.5;
      }

      .inline-banner__icon {
        flex-shrink: 0;
        margin-top: 1px;
      }

      .inline-banner__content {
        flex: 1;
        min-width: 0;
      }

      .inline-banner__content ::ng-deep strong {
        font-weight: 600;
      }

      .inline-banner__close {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin: -4px -4px -4px 0;
        padding: 0;
        border: none;
        border-radius: 4px;
        background: transparent;
        color: inherit;
        opacity: 0.6;
        cursor: pointer;
        transition: opacity 0.15s, background 0.15s;
      }

      .inline-banner__close:hover {
        opacity: 1;
        background: rgba(0, 0, 0, 0.06);
      }

      .inline-banner__close:focus-visible {
        opacity: 1;
        outline: 2px solid currentColor;
        outline-offset: 1px;
      }

      /* --- Type variants --- */
      .inline-banner--info {
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        color: #1e40af;
      }

      .inline-banner--warn {
        background: #fffbeb;
        border: 1px solid #fde68a;
        color: #92400e;
      }

      .inline-banner--success {
        background: #ecfdf3;
        border: 1px solid #a6f4c5;
        color: #065f46;
      }

      .inline-banner--error {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #991b1b;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineBannerComponent {
  @Input() type: BannerType = 'info';
  @Input() icon?: string;
  @Input() dismissable = false;

  @Output() dismissed = new EventEmitter<void>();

  get resolvedIcon(): string {
    if (this.icon) return this.icon;
    switch (this.type) {
      case 'success':
        return 'circle-check';
      case 'warn':
        return 'triangle-alert';
      case 'error':
        return 'circle-alert';
      default:
        return 'info';
    }
  }
}
